import { isUnknownRecord } from "@shared/validation/narrow";

export const AUTH_EVENT_COOKIE_NAME = "auth_event";
export const ONBOARDING_FLOW_COOKIE_NAME = "onboarding_flow";

export type OnboardingAccountType =
  | "buyer"
  | "vendor"
  | "freight_carrier"
  | "unknown";
export type OnboardingEntityType = "individual" | "business" | "unknown";
export type OnboardingIntent = "buy" | "sell" | "recycle" | "unknown";
export type ClaimContext = "none" | "claim" | "quote_claim";
export type SetupStep = 1 | 2;

export interface OnboardingFlowState {
  flow_id: string;
  started_at: string;
  entry_surface: string;
  return_to: string;
  intent: OnboardingIntent;
  product?: string;
  plan?: string;
  claim_context: ClaimContext;
  email_prefilled: boolean;
  signup_variant: "workos_redirect";
  account_type: OnboardingAccountType;
  entity_type: OnboardingEntityType;
  signup_completed_at?: string;
  setup_started_at?: string;
  setup_completed_at?: string;
  activation_completed_at?: string;
  viewed_steps: SetupStep[];
  completed_steps: SetupStep[];
}

interface CreateOnboardingFlowArgs {
  entry_surface?: string | null;
  return_to?: string | null;
  intent?: string | null;
  product?: string | null;
  plan?: string | null;
  claim_context?: string | null;
  email_prefilled?: boolean;
  signup_completed_at?: string;
}

function normalizeIntent(intent?: string | null): OnboardingIntent {
  if (intent === "buy" || intent === "sell" || intent === "recycle") {
    return intent;
  }
  return "unknown";
}

export function normalizeAccountType(
  accountType?: string | null,
): OnboardingAccountType {
  if (
    accountType === "buyer" ||
    accountType === "vendor" ||
    accountType === "freight_carrier"
  ) {
    return accountType;
  }
  return "unknown";
}

export function normalizeEntityType(entityType?: string | null): OnboardingEntityType {
  if (entityType === "individual" || entityType === "business") {
    return entityType;
  }
  return "unknown";
}

export function inferAccountTypeFromIntent(
  intent: OnboardingIntent,
): OnboardingAccountType {
  if (intent === "buy") return "buyer";
  if (intent === "sell" || intent === "recycle") return "vendor";
  return "unknown";
}

function normalizeClaimContext(claimContext?: string | null): ClaimContext {
  if (claimContext === "claim" || claimContext === "quote_claim") {
    return claimContext;
  }
  return "none";
}

function isAsciiAlphanumeric(char: string): boolean {
  const code = char.charCodeAt(0);
  return (code >= 48 && code <= 57) || (code >= 97 && code <= 122);
}

function normalizeEntrySurfaceSegment(segment: string): string {
  let normalized = "";

  for (const char of segment.toLowerCase()) {
    if (isAsciiAlphanumeric(char)) {
      normalized += char;
    } else if (normalized && !normalized.endsWith("_")) {
      normalized += "_";
    }
  }

  while (normalized.endsWith("_")) {
    normalized = normalized.slice(0, -1);
  }

  return normalized;
}

function normalizeEntrySurface(entrySurface?: string | null): string {
  if (!entrySurface) return "sign_up";

  const trimmedEntrySurface = entrySurface.trim();
  if (!trimmedEntrySurface) return "sign_up";

  const candidatePath = (() => {
    try {
      const url = new URL(trimmedEntrySurface);
      return url.pathname;
    } catch {
      return trimmedEntrySurface;
    }
  })();

  const sanitizedPath = candidatePath
    .split("/")
    .map(normalizeEntrySurfaceSegment)
    .filter(Boolean)
    .join("_");

  if (!sanitizedPath) return "home";
  return sanitizedPath;
}

export function serializeOnboardingFlow(flow: OnboardingFlowState): string {
  return JSON.stringify(flow);
}

function parseFlowJson(serializedFlow: string): unknown {
  try {
    return JSON.parse(serializedFlow);
  } catch {
    return JSON.parse(decodeURIComponent(serializedFlow));
  }
}

export function deserializeOnboardingFlow(
  serializedFlow: string,
): OnboardingFlowState | null {
  try {
    const parsed: unknown = parseFlowJson(serializedFlow);
    if (!isUnknownRecord(parsed)) return null;
    const { flow_id, started_at } = parsed;
    if (typeof flow_id !== "string" || typeof started_at !== "string") return null;

    return {
      flow_id,
      started_at,
      entry_surface: normalizeEntrySurface(optionalString(parsed.entry_surface)),
      return_to: optionalString(parsed.return_to) || "/dashboard",
      intent: normalizeIntent(optionalString(parsed.intent)),
      product: optionalString(parsed.product),
      plan: optionalString(parsed.plan),
      claim_context: normalizeClaimContext(optionalString(parsed.claim_context)),
      email_prefilled: parsed.email_prefilled === true,
      signup_variant: "workos_redirect",
      account_type:
        normalizeAccountType(optionalString(parsed.account_type)) === "unknown"
          ? inferAccountTypeFromIntent(normalizeIntent(optionalString(parsed.intent)))
          : normalizeAccountType(optionalString(parsed.account_type)),
      entity_type: normalizeEntityType(optionalString(parsed.entity_type)),
      signup_completed_at: optionalString(parsed.signup_completed_at),
      setup_started_at: optionalString(parsed.setup_started_at),
      setup_completed_at: optionalString(parsed.setup_completed_at),
      activation_completed_at: optionalString(parsed.activation_completed_at),
      viewed_steps: Array.isArray(parsed.viewed_steps)
        ? parsed.viewed_steps.filter(isSetupStep)
        : [],
      completed_steps: Array.isArray(parsed.completed_steps)
        ? parsed.completed_steps.filter(isSetupStep)
        : [],
    };
  } catch {
    return null;
  }
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

function isSetupStep(value: unknown): value is SetupStep {
  return value === 1 || value === 2;
}
export function getNowIsoString(): string {
  return new Date().toISOString();
}

export function createOnboardingFlow(
  args: CreateOnboardingFlowArgs,
): OnboardingFlowState {
  const intent = normalizeIntent(args.intent);

  return {
    flow_id: crypto.randomUUID(),
    started_at: getNowIsoString(),
    entry_surface: normalizeEntrySurface(args.entry_surface),
    return_to: args.return_to || "/dashboard",
    intent,
    product: args.product || undefined,
    plan: args.plan || undefined,
    claim_context: normalizeClaimContext(args.claim_context),
    email_prefilled: args.email_prefilled === true,
    signup_variant: "workos_redirect",
    account_type: inferAccountTypeFromIntent(intent),
    entity_type: "unknown",
    signup_completed_at: args.signup_completed_at,
    viewed_steps: [],
    completed_steps: [],
  };
}
